import { detectWakeAny } from '@elena/shared';
import {
  isMicBusy,
  isOwnVoice,
  onMicBusy,
  onSpeaking,
  recognitionLang,
  RecognitionCtor,
  type Recognition,
} from './speech';

/**
 * "Hej Elena" in the browser: continuous Serbian recognition (Chrome / Edge) that only
 * reacts to phrases containing Elena's name. It keeps listening while Elena talks, so
 * "Hej Elena" cuts her off (her own words are ignored); it steps aside only while the mic
 * button is in use, and restarts itself when the browser ends the session.
 */

export type WakeState = 'off' | 'listening' | 'paused' | 'error';

export interface WakeHandlers {
  onWake: (command: string) => void;
  onState: (state: WakeState, message?: string) => void;
  /** Every finished phrase the recognizer heard (best guess), to show what it understood. */
  onHeard?: (text: string) => void;
  /** A recognizer problem that is retried (network, aborted by another window...). */
  onIssue?: (code: string) => void;
}

const fatal: Record<string, string> = {
  'not-allowed': 'Dozvoli mikrofon za ovu stranicu da bi „Hej Elena“ radilo.',
  'service-not-allowed': 'Browser ne dozvoljava prepoznavanje govora.',
  'language-not-supported': 'Browser nema srpski za prepoznavanje govora.',
  'audio-capture': 'Ne vidim mikrofon na ovom uređaju.',
};

export const canWake = (): boolean => Boolean(RecognitionCtor());

export class WakeListener {
  private rec: Recognition | null = null;
  private running = false;
  private failures = 0;
  private timer: number | undefined;
  /** After a wake, leave the mic alone briefly so the command can be heard by others. */
  private quietUntil = 0;
  private unwatch: Array<() => void> = [];
  /** The "only one window listens" lock (see start). */
  private lockAbort: AbortController | undefined;
  private releaseLock: (() => void) | undefined;

  /** priority: the PC widget, which takes over listening from a tab (tabs lose the mic in the background). */
  constructor(
    private readonly h: WakeHandlers,
    private readonly priority = false,
  ) {}

  start(): void {
    const Ctor = RecognitionCtor();
    if (!Ctor) {
      this.h.onState('error', '„Hej Elena“ radi u Chrome-u i Edge-u.');
      return;
    }
    if (this.running) return;
    this.running = true;

    // Chrome runs one speech recognition at a time: two windows listening for "Hej Elena" (the
    // PC widget and a tab) abort each other forever and neither hears anything. Only the window
    // holding this lock listens; the others wait and take over when it closes.
    if ('locks' in navigator) this.acquire(Ctor, this.priority);
    else this.listen(Ctor);
  }

  private acquire(Ctor: new () => Recognition, steal: boolean): void {
    this.h.onState('paused');
    const lockAbort = new AbortController();
    this.lockAbort = lockAbort;
    navigator.locks
      .request(
        'elena-wake',
        // the widget takes the lock even from a tab that holds it; a tab waits its turn
        steal ? { steal: true } : { signal: lockAbort.signal },
        () =>
          new Promise<void>((release) => {
            this.releaseLock = release;
            if (this.running) this.listen(Ctor);
            else release();
          }),
      )
      .catch(() => {
        // stopped while waiting, or the widget took over: step aside and wait for it to close
        if (!this.running || lockAbort.signal.aborted) return;
        this.quiet();
        this.acquire(Ctor, false);
      });
  }

  /** Stops listening without giving up (the lock may come back). */
  private quiet(): void {
    window.clearTimeout(this.timer);
    this.unwatch.forEach((u) => u());
    this.unwatch = [];
    try {
      this.rec?.abort();
    } catch {
      // not started
    }
    this.rec = null;
    this.releaseLock = undefined;
  }

  private listen(Ctor: new () => Recognition): void {
    const rec = new Ctor();
    rec.lang = recognitionLang();
    rec.continuous = true;
    rec.interimResults = false;
    // the name is often only in the 2nd or 3rd guess ("Alena", "hej lena")
    rec.maxAlternatives = 5;
    rec.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (!r?.isFinal) continue;
        this.failures = 0;
        const guesses: string[] = [];
        for (let k = 0; k < r.length; k++) guesses.push(r[k]?.transcript ?? '');
        const wake = detectWakeAny(guesses);
        this.h.onHeard?.(guesses[0] ?? '');
        // while Elena talks the mic hears her too: her own words never wake her
        if (wake.heard && isOwnVoice(guesses[0] ?? '')) continue;
        if (wake.heard) {
          // Hand the mic over right away; the command or the next phrase is Elena's now.
          this.quietUntil = Date.now() + 1500;
          rec.abort();
          this.h.onWake(wake.command);
          return;
        }
      }
    };
    rec.onerror = (e) => {
      const message = fatal[e.error];
      if (message) {
        this.stop();
        this.h.onState('error', message);
      } else if (e.error !== 'no-speech' && e.error !== 'aborted') {
        this.failures++;
        this.h.onIssue?.(e.error);
      }
    };
    rec.onend = () => this.resume();
    this.rec = rec;

    // Pause while the mic button listens (it needs the one recognizer), resume right after.
    const sync = () => {
      if (!this.running) return;
      if (isMicBusy()) {
        window.clearTimeout(this.timer);
        try {
          rec.abort();
        } catch {
          // not started
        }
        this.h.onState('paused');
      } else {
        this.resume();
      }
    };
    this.unwatch = [onMicBusy(sync), onSpeaking(sync)];
    // Browsers stop the mic in background tabs; pick up again when the tab returns.
    const onVisible = () => document.visibilityState === 'visible' && this.resume();
    document.addEventListener('visibilitychange', onVisible);
    this.unwatch.push(() => document.removeEventListener('visibilitychange', onVisible));

    this.resume();
  }

  stop(): void {
    this.running = false;
    this.lockAbort?.abort();
    this.lockAbort = undefined;
    this.releaseLock?.();
    this.quiet();
    this.h.onState('off');
  }

  private resume(): void {
    if (!this.running || !this.rec || isMicBusy()) return;
    window.clearTimeout(this.timer);
    const backoff = this.failures === 0 ? 200 : Math.min(30_000, 1000 * 2 ** this.failures);
    const delay = Math.max(backoff, this.quietUntil - Date.now());
    this.timer = window.setTimeout(() => {
      if (!this.running || !this.rec || isMicBusy()) return;
      try {
        this.rec.lang = recognitionLang(); // the conversation language may have changed since
        this.rec.start();
        this.h.onState('listening');
      } catch {
        // Already running; onend will bring us back here.
      }
    }, delay);
  }
}
